import React from "react";
import { useSearchParams } from "react-router-dom";
import { pricingCategory, priceTypes } from "../../../enums/pricing.enum";
import { currencyDisplay } from "../../../utils/helpers";

interface PriceChartV3Props {
  priceCurrency: string;
  prices: any[];
  roomTypes?: Record<string, string>;
  isB2C2B?: boolean;
}

const PROMO_TYPES = new Set(["specialoffer", "specialdeal", "superpromo", "early_bird"]);

const isPromoType = (type: string) =>
  type?.startsWith("promo") || PROMO_TYPES.has(type);

// Split "Adult (Single Room)" → ["Adult", "(Single Room)"]
const parseCatLabel = (label: string): [string, string | null] => {
  const match = label.match(/^(.+?)\s*(\(.+\))$/);
  return match ? [match[1].trim(), match[2]] : [label, null];
};

const PriceChartV3: React.FC<PriceChartV3Props> = ({ priceCurrency, prices, roomTypes }) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const categories = pricingCategory(roomTypes || type || undefined);

  const priceLabel = (val: any) =>
    val == null || val === "" || Number(val) === 0
      ? "TBC"
      : currencyDisplay(priceCurrency, val);

  const tierLabel = (price: any) =>
    price.display_price || priceTypes[price.type] || price.type;

  // With a single tier the fixed-width columns leave the bordered box half-empty;
  // a modifier lets the lone tier column stretch to fill the drawer width.
  const isSingleTier = prices.length === 1;

  return (
    <div className={`v3-price-chart${isSingleTier ? " v3-price-chart--single" : ""}`}>
      <div className="v3-price-chart_table">
        {/* Sticky category column */}
        <div className="v3-price-chart_col v3-price-chart_col--category">
          <div className="v3-price-chart_header v3-price-chart_header--category">
            Category
          </div>
          {Object.entries(categories).map(([key, label]) => {
            const [main, sub] = parseCatLabel(label);
            return (
              <div key={key} className="v3-price-chart_cell v3-price-chart_cell--category">
                <span className="v3-price-chart_cat-main">{main}</span>
                {sub && <span className="v3-price-chart_cat-sub">{sub}</span>}
              </div>
            );
          })}
        </div>

        {/* Scrollable price tier columns */}
        {prices.map((price) => {
          const promo = isPromoType(price.type);
          return (
            <div
              key={price.type}
              className={`v3-price-chart_col${promo ? " v3-price-chart_col--promo" : " v3-price-chart_col--normal"}`}
            >
              <div
                className={`v3-price-chart_header v3-price-chart_header--tier${promo ? " v3-price-chart_header--promo" : ""}`}
              >
                <span className="v3-price-chart_tier-label">{tierLabel(price)}</span>
                {promo && <span className="v3-price-chart_badge">PROMO</span>}
              </div>
              {Object.keys(categories).map((cat) => (
                <div
                  key={cat}
                  className={`v3-price-chart_cell${promo ? " v3-price-chart_cell--promo" : ""}`}
                >
                  {priceLabel(price[cat])}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriceChartV3;
